import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import PrivateRoute, { LoadingFallback } from './PrivateRoute';
import { useAuthValue } from '../contexts/AuthContext';
import { useUserAPI } from '../contexts/UserAPIContext';

import Login from '../pages/usuarios/Login';
import Home from '../pages/dashboard/Home';
import Avisos from '../pages/avisos/Avisos';
import NovoAviso from '../pages/avisos/NovoAviso';
import EditarAviso from '../pages/avisos/EditarAviso';
import ExcluirAviso from '../pages/avisos/ExcluirAviso';
import Calendario from '../pages/calendario/Calendario';
import GerenciamentoCalendario from '../pages/calendario/GerenciamentoCalendario';
import Reposicoes from '../pages/calendario/Reposicoes';
import DetalhesReposicao from '../pages/calendario/DetalhesReposicao';
import Disponibilidade from '../pages/calendario/Disponibilidade';
import Terapeutas from '../pages/terapeutas/Terapeutas';
import PerfilTerapeuta from '../pages/terapeutas/PerfilTerapeuta';
import EditarTerapeuta from '../pages/terapeutas/EditarTerapeuta';
import NovaTerapeuta from '../pages/terapeutas/NovaTerapeuta';
import GerarUsuarioTerap from '../pages/terapeutas/GerarUsuarioTerap';
import EscalaTerapeuta from '../pages/terapeutas/EscalaTerapeuta';
import EscalasTerapeutas from '../pages/terapeutas/EscalasTerapeutas';
import Pacientes from '../pages/pacientes/Pacientes';
import PacientesBeta from '../pages/pacientes/PacientesBeta';
import PerfilPaciente from '../pages/pacientes/PerfilPaciente';
import EditarPaciente from '../pages/pacientes/EditarPaciente';
import NovoPaciente from '../pages/pacientes/NovoPaciente';
import Responsaveis from '../pages/responsaveis/Responsaveis';
import NovoResponsavel from '../pages/responsaveis/NovoResponsavel';
import VisualizarResponsavel from '../pages/responsaveis/VisualizarResponsavel';
import EditarResponsavel from '../pages/responsaveis/EditarResponsavel';
import ExcluirResponsavel from '../pages/responsaveis/ExcluirResponsavel';
import GerarUsuarioResp from '../pages/responsaveis/GerarUsuarioResp';
import Funcoes from '../pages/funcoes/Funcoes';
import NovaFuncao from '../pages/funcoes/NovaFuncao';
import EditarFuncao from '../pages/funcoes/EditarFuncao';
import ExcluirFuncao from '../pages/funcoes/ExcluirFuncao';
import PrestServicos from '../pages/prestservicos/PrestServicos';
import NovoPrestServico from '../pages/prestservicos/NovoPrestServico';
import VisualizarPrestServico from '../pages/prestservicos/VisualizarPrestServico';
import EditarPrestServico from '../pages/prestservicos/EditarPrestServico';
import ExcluirPrestServico from '../pages/prestservicos/ExcluirPrestServico';
import Planos from '../pages/planos/Planos';
import NovoPlano from '../pages/planos/NovoPlano';
import EditarPlano from '../pages/planos/EditarPlano';
import ExcluirPlano from '../pages/planos/ExcluirPlano';
import Exportacoes from '../pages/exportacoes/Exportacoes';
import Usuarios from '../pages/usuarios/Usuarios';
import NovoUsuario from '../pages/usuarios/NovoUsuario';
import EditarUsuario from '../pages/usuarios/EditarUsuario';
import AlterarSenha from '../pages/usuarios/AlterarSenha';
import ExcluirUsuario from '../pages/usuarios/ExcluirUsuario';
import Salas from '../pages/salas/Salas';
import NovaSala from '../pages/salas/NovaSala';
import EditarSala from '../pages/salas/EditarSala';
import ExcluirSala from '../pages/salas/ExcluirSala';
import Triagens from '../pages/triagens/Triagens';
import NovaTriagem from '../pages/triagens/NovaTriagem';
import EditarTriagem from '../pages/triagens/EditarTriagem';
import VisualizarTriagem from '../pages/triagens/VisualizarTriagem';
import ExcluirTriagem from '../pages/triagens/ExcluirTriagem';
import TesteNotificacao from '../pages/testes/TesteNotificacao';

const LoginRoute = () => {
  const location = useLocation();
  const { user, authLoading } = useAuthValue();
  const { userAPIData, loading } = useUserAPI();

  if (authLoading || loading) {
    return <LoadingFallback />;
  }

  if (user && userAPIData) {
    const fromState = location.state?.from?.pathname;
    const roleId = userAPIData.userRole?.id || userAPIData.userRoleId;
    
    if (Number(roleId) === 5) {
      return <Navigate to={fromState || '/Exportacoes'} replace />;
    }
    if (Number(roleId) === 6) {
      return <Navigate to={fromState || '/Calendario'} replace />;
    }
    return <Navigate to={fromState || '/'} replace />;
  }

  return (
    <div className="auth-wrapper">
      <Login />
    </div>
  );
};

const HomeRoute = () => {
  const { userAPIData } = useUserAPI();

  const roleId = userAPIData?.userRole?.id || userAPIData?.userRoleId;

  if (Number(roleId) === 5) {
    return <Navigate to="/Exportacoes" replace />;
  }

  if (Number(roleId) === 6) {
    return <Navigate to="/Calendario" replace />;
  }

  return <Home />;
};

// Componente para proteger rotas - role 6 só pode acessar /Calendario
const ProtectedRouteForRole6 = ({ children }) => {
  const { userAPIData } = useUserAPI();

  // Verificação explícita e defensiva
  const roleId = userAPIData?.userRole?.id || userAPIData?.userRoleId;

  if (Number(roleId) === 6) {
    return <Navigate to="/Calendario" replace />;
  }

  return children;
};

// Componente para proteger rotas exclusivas de administrador (role 1)
const ProtectedRouteForAdmin = ({ children }) => {
  const { userAPIData } = useUserAPI();

  const roleId = userAPIData?.userRole?.id || userAPIData?.userRoleId;

  if (Number(roleId) !== 1) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const TRIAGEM_ALLOWED_ROLES = [1, 2, 4];

// Componente para proteger rotas de triagem (roles 1, 2 e 4)
const ProtectedRouteForTriagem = ({ children }) => {
  const { userAPIData } = useUserAPI();

  const roleId = Number(userAPIData?.userRole?.id || userAPIData?.userRoleId);

  if (!TRIAGEM_ALLOWED_ROLES.includes(roleId)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginRoute />} />

    <Route element={<PrivateRoute />}>
      <Route element={<Navbar />}>
        <Route index element={<HomeRoute />} />

        <Route path="/Avisos" element={<ProtectedRouteForRole6><Avisos /></ProtectedRouteForRole6>} />
        <Route path="/Avisos/Novo" element={<ProtectedRouteForRole6><NovoAviso /></ProtectedRouteForRole6>} />
        <Route path="/Avisos/Editar/:id" element={<ProtectedRouteForRole6><EditarAviso /></ProtectedRouteForRole6>} />
        <Route path="/Avisos/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirAviso /></ProtectedRouteForRole6>} />

        <Route path="/Calendario" element={<Calendario />} />
        <Route path="/Calendario/Antigo" element={<Calendario />} />
        <Route path="/Calendario/Disponibilidade" element={<Disponibilidade />} />
        <Route path="/Calendario/Gerenciamento" element={<ProtectedRouteForRole6><GerenciamentoCalendario /></ProtectedRouteForRole6>} />
        <Route path="/Calendario/Reposicoes" element={<ProtectedRouteForRole6><Reposicoes /></ProtectedRouteForRole6>} />
        <Route path="/Calendario/Reposicoes/Detalhes/Paciente/:id" element={<ProtectedRouteForRole6><DetalhesReposicao /></ProtectedRouteForRole6>} />

        <Route path="/Terapeutas" element={<ProtectedRouteForRole6><Terapeutas /></ProtectedRouteForRole6>} />
        <Route path="/Terapeutas/PerfilTerapeuta/:id" element={<ProtectedRouteForRole6><PerfilTerapeuta /></ProtectedRouteForRole6>} />
        <Route path="/Terapeutas/Editar/:id" element={<ProtectedRouteForRole6><EditarTerapeuta /></ProtectedRouteForRole6>} />
        <Route path="/Terapeutas/Novo" element={<ProtectedRouteForRole6><NovaTerapeuta /></ProtectedRouteForRole6>} />
        <Route path="/Terapeutas/GerarUsuario/:id" element={<ProtectedRouteForRole6><GerarUsuarioTerap /></ProtectedRouteForRole6>} />
        <Route path="/Terapeutas/Escala" element={<ProtectedRouteForAdmin><EscalasTerapeutas /></ProtectedRouteForAdmin>} />
        <Route path="/Terapeutas/Escala/:id" element={<ProtectedRouteForAdmin><EscalaTerapeuta /></ProtectedRouteForAdmin>} />

        <Route path="/Funcoes" element={<ProtectedRouteForRole6><Funcoes /></ProtectedRouteForRole6>} />
        <Route path="/Funcoes/Novo" element={<ProtectedRouteForRole6><NovaFuncao /></ProtectedRouteForRole6>} />
        <Route path="/Funcoes/Editar/:id" element={<ProtectedRouteForRole6><EditarFuncao /></ProtectedRouteForRole6>} />
        <Route path="/Funcoes/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirFuncao /></ProtectedRouteForRole6>} />

        <Route path="/Pacientes" element={<ProtectedRouteForRole6><Pacientes /></ProtectedRouteForRole6>} />
        <Route path="/PacientesBeta" element={<ProtectedRouteForRole6><PacientesBeta /></ProtectedRouteForRole6>} />
        <Route path="/Pacientes/PerfilPaciente/:id" element={<ProtectedRouteForRole6><PerfilPaciente /></ProtectedRouteForRole6>} />
        <Route path="/Pacientes/Editar/:id" element={<ProtectedRouteForRole6><EditarPaciente /></ProtectedRouteForRole6>} />
        <Route path="/Pacientes/Novo" element={<ProtectedRouteForRole6><NovoPaciente /></ProtectedRouteForRole6>} />

        <Route path="/Responsaveis" element={<ProtectedRouteForRole6><Responsaveis /></ProtectedRouteForRole6>} />
        {/* <Route path="/Responsaveis/Novo" element={<NovoResponsavel />} /> */}
        <Route path="/Responsaveis/Visualizar/:id" element={<ProtectedRouteForRole6><VisualizarResponsavel /></ProtectedRouteForRole6>} />
        <Route path="/Responsaveis/Editar/:id" element={<ProtectedRouteForRole6><EditarResponsavel /></ProtectedRouteForRole6>} />
        <Route path="/Responsaveis/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirResponsavel /></ProtectedRouteForRole6>} />
        <Route path="/Responsaveis/GerarUsuario/:id" element={<ProtectedRouteForRole6><GerarUsuarioResp /></ProtectedRouteForRole6>} />

        <Route path="/PrestServicos" element={<ProtectedRouteForRole6><PrestServicos /></ProtectedRouteForRole6>} />
        <Route path="/PrestServicos/Novo" element={<ProtectedRouteForRole6><NovoPrestServico /></ProtectedRouteForRole6>} />
        <Route path="/PrestServicos/Visualizar/:id" element={<ProtectedRouteForRole6><VisualizarPrestServico /></ProtectedRouteForRole6>} />
        <Route path="/PrestServicos/Editar/:id" element={<ProtectedRouteForRole6><EditarPrestServico /></ProtectedRouteForRole6>} />
        <Route path="/PrestServicos/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirPrestServico /></ProtectedRouteForRole6>} />

        <Route path="/Planos" element={<ProtectedRouteForRole6><Planos /></ProtectedRouteForRole6>} />
        <Route path="/Planos/Novo" element={<ProtectedRouteForRole6><NovoPlano /></ProtectedRouteForRole6>} />
        <Route path="/Planos/Editar/:id" element={<ProtectedRouteForRole6><EditarPlano /></ProtectedRouteForRole6>} />
        <Route path="/Planos/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirPlano /></ProtectedRouteForRole6>} />

        <Route path="/Exportacoes" element={<ProtectedRouteForRole6><Exportacoes /></ProtectedRouteForRole6>} />

        <Route path="/Usuarios" element={<ProtectedRouteForRole6><Usuarios /></ProtectedRouteForRole6>} />
        <Route path="/Usuarios/NovoUsuario" element={<ProtectedRouteForRole6><NovoUsuario /></ProtectedRouteForRole6>} />
        <Route path="/Usuarios/Editar/:id" element={<ProtectedRouteForRole6><EditarUsuario /></ProtectedRouteForRole6>} />
        <Route path="/Usuarios/AlterarSenha/:id" element={<ProtectedRouteForRole6><AlterarSenha /></ProtectedRouteForRole6>} />
        <Route path="/Usuarios/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirUsuario /></ProtectedRouteForRole6>} />

        <Route path="/Salas" element={<ProtectedRouteForRole6><Salas /></ProtectedRouteForRole6>} />
        <Route path="/Salas/Nova" element={<ProtectedRouteForRole6><NovaSala /></ProtectedRouteForRole6>} />
        <Route path="/Salas/Editar/:id" element={<ProtectedRouteForRole6><EditarSala /></ProtectedRouteForRole6>} />
        <Route path="/Salas/Excluir/:id" element={<ProtectedRouteForRole6><ExcluirSala /></ProtectedRouteForRole6>} />

        <Route path="/Triagens" element={<ProtectedRouteForTriagem><Triagens /></ProtectedRouteForTriagem>} />
        <Route path="/Triagens/Novo" element={<ProtectedRouteForTriagem><NovaTriagem /></ProtectedRouteForTriagem>} />
        <Route path="/Triagens/Visualizar/:id" element={<ProtectedRouteForTriagem><VisualizarTriagem /></ProtectedRouteForTriagem>} />
        <Route path="/Triagens/Editar/:id" element={<ProtectedRouteForTriagem><EditarTriagem /></ProtectedRouteForTriagem>} />
        <Route path="/Triagens/Excluir/:id" element={<ProtectedRouteForTriagem><ExcluirTriagem /></ProtectedRouteForTriagem>} />

        <Route path="/TesteNotificacao" element={<ProtectedRouteForRole6><TesteNotificacao /></ProtectedRouteForRole6>} />
      </Route>
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default AppRoutes;

